import type { z } from 'zod';

// Owner: shell. A value from a native control (`<select>`, radio) as one of the choices it was rendered
// with: the DOM hands back a string, this gives back the choice — or undefined for anything else.

/** `value` when it is one of `choices`, else undefined. */
export function oneOf<const T extends string>(value: string, choices: readonly T[]): T | undefined {
  return choices.find((choice) => choice === value);
}

/** `value` read through a zod schema of the choices (an enum): the choice, or undefined for anything else. */
export function choiceOf<S extends z.ZodType>(schema: S, value: unknown): z.output<S> | undefined {
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** `value` as `<head><separator><rest>` where `head` is one of `heads` (a composite option value); null otherwise. */
export function splitChoice<const T extends string>(
  value: string,
  separator: string,
  heads: readonly T[],
): [head: T, rest: string] | null {
  const at = value.indexOf(separator);
  const head = at < 0 ? undefined : oneOf(value.slice(0, at), heads);
  return head === undefined ? null : [head, value.slice(at + separator.length)];
}

/** `value` as `<first><separator><second>` (a composite option value); null without a separator. */
export function splitPair(value: string, separator: string): [first: string, second: string] | null {
  const at = value.indexOf(separator);
  return at < 0 ? null : [value.slice(0, at), value.slice(at + separator.length)];
}
